import React from "react";
import { Message, Widget } from "@/types";
import { TextMessage } from "./types/TextMessage";
import { ImageMessage } from "./types/ImageMessage";
import { AudioMessage } from "./types/AudioMessage";
import { WidgetMessage } from "./types/WidgetMessage";
import { VideoMessage } from "./types/VideoMessage";
import { DocumentMessage } from "./types/DocumentMessage";
import { cn, getFileUrl } from "@/lib/utils";
import { useCurrentChatId } from "@/hooks";
import { useChatAccessStore } from "@/features/chat/store/chat-access-store";
import { MessageModelSuggestions } from "./MessageModelSuggestions";
import { WebProductCarouselWidget } from "@/components/widgets/WebProductCarouselWidget";
import { MessageExternalLinks } from "./MessageExternalLinks";
import { ComparisonButton } from "@/features/product-comparison/components/ComparisonButton";

interface MessageRendererProps {
  message: Message;
  onModelSelect?: (model: Widget) => void;
  isLastAssistantMessage?: boolean;
  onEdit?: (messageId: string) => void;
}

const MessageRendererBase: React.FC<MessageRendererProps> = ({
  message,
  onModelSelect,
  isLastAssistantMessage = true,
  onEdit,
}) => {
  const hasProducts = message.products && message.products.length > 0;
  const hasProductGroups =
    message.resultGroups && message.resultGroups.length > 0;

  // 🔒 Shared read-only mod kontrolü
  const currentChatId = useCurrentChatId();
  const chatAccess = useChatAccessStore((state) =>
    currentChatId ? state.byChatId[currentChatId] : undefined
  );
  const isReadOnly = chatAccess?.canEdit === false;

  return (
    <div className={cn("space-y-4")}>
      {/* Image Content - User mesajlarında text'ten önce göster */}
      {message.role === "user" &&
        message.images &&
        message.images.length > 0 && (
          <ImageMessage
            images={message.images}
            messageId={message.id}
            role="user"
          />
        )}

      {/* Text Content */}
      {message.content && (
        <TextMessage
          key={message.id}
          content={message.content}
          role={message.role}
        />
      )}

      {/* Audio/Video Content */}
      {message.audioUrl &&
        (message.audioUrl.toLowerCase().endsWith(".mp4") ? (
          <VideoMessage url={message.audioUrl} prompt={message.prompt} />
        ) : (
          <AudioMessage url={message.audioUrl} prompt={message.prompt} />
        ))}

      {/* Image Content - Assistant mesajlarında text'ten sonra göster */}
      {message.role === "assistant" &&
        message.images &&
        message.images.length > 0 && (
          <ImageMessage
            images={message.images}
            messageId={message.id}
            role="assistant"
          />
        )}

      {/* Video Content (generatedContent fallback) */}
      {message.generatedContent &&
        typeof message.generatedContent === "string" &&
        message.generatedContent.toLowerCase().endsWith(".mp4") && (
          <VideoMessage
            url={message.generatedContent}
            prompt={message.prompt}
          />
        )}

      {/* Library Content (R2 creative generation sonuçları) */}
      {message.libraryItems &&
        message.libraryItems.length > 0 &&
        (() => {
          // Tüm library items'ları type'a göre grupla
          const libraryImages: string[] = [];
          const libraryVideos: Array<{
            url: string;
            title: string;
            _id: string;
          }> = [];
          const libraryAudios: Array<{
            url: string;
            title: string;
            _id: string;
          }> = [];
          const libraryDocuments: Array<{
            url: string;
            title: string;
            _id: string;
            size?: number;
            extension?: string;
          }> = [];

          message.libraryItems.forEach((item) => {
            const fileUrl = getFileUrl(item.file);
            if (!fileUrl) return;

            if (item.type === "image") {
              libraryImages.push(fileUrl);
            } else if (item.type === "video") {
              libraryVideos.push({
                url: fileUrl,
                title: item.title,
                _id: item._id,
              });
            } else if (item.type === "audio") {
              libraryAudios.push({
                url: fileUrl,
                title: item.title,
                _id: item._id,
              });
            } else if (item.type === "pdf" || item.type === "document") {
              // DeepL document translations land here.
              const ext =
                item.title?.split(".").pop()?.toLowerCase() ||
                (item.type === "pdf" ? "pdf" : undefined);
              libraryDocuments.push({
                url: fileUrl,
                title: item.title,
                _id: item._id,
                size: item.file?.size,
                extension: ext,
              });
            }
          });

          return (
            <>
              {/* Tüm image'ları tek bir ImageMessage'da göster (ScatteredImageCarousel için) */}
              {libraryImages.length > 0 && (
                <ImageMessage
                  images={libraryImages}
                  messageId={message.id}
                  onEdit={onEdit}
                  role={message.role}
                />
              )}

              {/* Video items */}
              {libraryVideos.map((video) => (
                <VideoMessage
                  key={video._id}
                  url={video.url}
                  prompt={video.title}
                />
              ))}

              {/* Audio items */}
              {libraryAudios.map((audio) => (
                <AudioMessage
                  key={audio._id}
                  url={audio.url}
                  prompt={audio.title}
                />
              ))}

              {/* Document items (translated PDFs / docs) */}
              {libraryDocuments.map((doc) => (
                <DocumentMessage
                  key={doc._id}
                  url={doc.url}
                  title={doc.title}
                  size={doc.size}
                  extension={doc.extension}
                />
              ))}
            </>
          );
        })()}

      {/* Widget Content */}
      {(message.widgets && message.widgets.length > 0) ||
      hasProducts ||
      hasProductGroups ||
      (message.productIds && message.productIds.length > 0) ? (
        <WidgetMessage
          widgets={message.widgets || []}
          selectedModel={message.selectedModel}
          onModelSelect={onModelSelect}
          canSelectModel={
            message.role === "assistant" &&
            isLastAssistantMessage &&
            !isReadOnly
          }
          products={message.products}
          productIds={message.productIds}
          offerMap={message.offerMap}
          filterOptions={message.filterOptions}
          resultGroups={message.resultGroups}
        />
      ) : null}

      {/* Web search product cards (katalog dışı). Gruplar kendi web
          kartlarını taşıdığı için bu düz şerit yalnız grupsuz cevaplarda
          çizilir; aksi halde aynı kartlar iki kez görünür. */}
      {!hasProductGroups &&
        message.webProducts &&
        message.webProducts.length > 0 && (
          <WebProductCarouselWidget
            products={message.webProducts}
            query={message.webSearchQuery}
          />
        )}

      {/* Turun sunduğu uygulama dışı bağlantılar (kargo takip, fatura).
          Stream sırasında da aynı şerit çiziliyor; yenilemeden sonra
          kaybolmaması için backend kaydından okunuyor. */}
      <MessageExternalLinks links={message.externalLinks} />

      {message.productComparison && (
        <ComparisonButton comparison={message.productComparison} />
      )}

      {/* Model Suggestions - Self-contained component */}
      <MessageModelSuggestions message={message} />
    </div>
  );
};

// Streaming sırasında her token'da geçmiş mesajların yeniden render
// edilmesini önlemek için memoize edilir. Mesaj objeleri backend cache'inden
// referans-stabil geldiği için shallow compare yeterlidir.
export const MessageRenderer = React.memo(MessageRendererBase);
